<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { apps, Models, useRegistry, useContext, useDialogs, useLocalization } from '@hantera/portal-app'
import AppSlotView from '@/AppSlotView.vue'
import AddressEditor from '@/components/AddressEditor'
import { Expandable } from '@hantera/design-system'

const t = useLocalization('settings.inventories')
const context = useContext()
const registry = useRegistry()
const dialogs = useDialogs()

const canWrite = computed(() => context.authorize('registry/inventories:write'))

const props = defineProps<{
  inventoryKey: string
  data: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'deleted'): void
}>()

const SYSTEM_FIELDS = ['label', 'address']

function blankAddress(): Models.Graph.Address {
  return {
    name: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    countryCode: '',
    state: '',
    email: '',
    phone: '',
    careOf: '',
  } as Models.Graph.Address
}

function isAddressEmpty(addr: Models.Graph.Address): boolean {
  return Object.values(addr).every(v => typeof v !== 'string' || v.trim().length === 0)
}

const label = ref<string | Record<string, string>>('')
const address = ref<Models.Graph.Address>(blankAddress())
const pendingAppFields = ref<Record<string, any>>({})
const removedAppFields = ref<Set<string>>(new Set())
const saving = ref(false)


watch(() => [props.inventoryKey, props.data], () => {
  resetForm()
}, { immediate: true })

function resetForm() {
  label.value = props.data?.label ?? ''
  address.value = { ...blankAddress(), ...(props.data?.address ?? {}) }
  pendingAppFields.value = {}
  removedAppFields.value = new Set()
}

const originalAddress = computed(() => ({ ...blankAddress(), ...(props.data?.address ?? {}) }))

const isDirty = computed(() => {
  const originalLabel = props.data?.label ?? ''
  return JSON.stringify(label.value) !== JSON.stringify(originalLabel) ||
    JSON.stringify(address.value) !== JSON.stringify(originalAddress.value) ||
    Object.keys(pendingAppFields.value).length > 0 ||
    removedAppFields.value.size > 0
})


const slotData = computed(() => {
  const base = { ...(props.data ?? {}) }
  for (const [key, value] of Object.entries(pendingAppFields.value)) {
    base[key] = value
  }
  for (const key of removedAppFields.value) {
    delete base[key]
  }
  return base
})

function setAppField(field: string, value: any) {
  if (SYSTEM_FIELDS.includes(field)) return
  pendingAppFields.value[field] = value
  removedAppFields.value.delete(field)
}

function removeAppField(field: string) {
  if (SYSTEM_FIELDS.includes(field)) return
  delete pendingAppFields.value[field]
  removedAppFields.value.add(field)
}

async function save() {
  if (!canWrite.value || saving.value) return

  saving.value = true
  try {
    const record: Record<string, any> = {}

    for (const [key, value] of Object.entries(props.data ?? {})) {
      if (!SYSTEM_FIELDS.includes(key)) {
        record[key] = value
      }
    }

    for (const [key, value] of Object.entries(pendingAppFields.value)) {
      record[key] = value
    }

    for (const key of removedAppFields.value) {
      delete record[key]
    }

    record.label = typeof label.value === 'string' ? label.value.trim() : label.value

    if (!isAddressEmpty(address.value)) {
      const trimmed: Record<string, any> = {}
      for (const [k, v] of Object.entries(address.value)) {
        trimmed[k] = typeof v === 'string' ? v.trim() : v
      }
      record.address = trimmed
    }

    await registry.set(`inventories/${props.inventoryKey}`, record)
    pendingAppFields.value = {}
    removedAppFields.value = new Set()
    emit('saved')
  } catch (e) {
    console.error('Failed to save inventory:', e)
  } finally {
    saving.value = false
  }
}

async function deleteInventory() {
  const confirmed = await dialogs.confirm(
    t('editor.deleteConfirm').replace('{key}', props.inventoryKey),
    t('editor.deleteTitle'),
    t('editor.delete'),
    undefined,
    true
  )
  if (!confirmed) return

  try {
    await registry.clear(`inventories/${props.inventoryKey}`)
    emit('deleted')
  } catch (e) {
    console.error('Failed to delete inventory:', e)
  }
}
</script>

<template>
  <div class="inventory-editor">
    <h2>{{ inventoryKey }}</h2>

    <fieldset>
      <div>
        <label>{{ t('editor.label') }}</label>
        <LocalizedLabelInput
          v-model="label"
          :disabled="!canWrite"
        />
      </div>

      <div class="box-content p">
        <Expandable>
          <template #label>
            {{ t('editor.addressSection') }}
          </template>
          <template #default>
            <div :inert="!canWrite" :style="!canWrite ? { opacity: 0.6 } : undefined">
              <AddressEditor v-model="address" :key="inventoryKey" />
            </div>
          </template>
        </Expandable>
      </div>
    </fieldset>

    

    <AppSlotView
      :slot="apps.inventoryEditorSlots.inventory.editor"
      :context="{
        inventoryKey: inventoryKey,
        data: slotData,
        setField: setAppField,
        removeField: removeAppField,
      }"
    />

    <div class="inventory-editor__actions" v-if="canWrite">
      <button class="btn-primary" :disabled="!isDirty || saving" @click="save">
        {{ t('editor.save') }}
      </button>
      <button :disabled="!isDirty" @click="resetForm">
        {{ t('editor.reset') }}
      </button>
      <button class="btn-danger" @click="deleteInventory">
        {{ t('editor.delete') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.inventory-editor {
  padding: 20px;
  max-width: 600px;
}

.inventory-editor h2 {
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 20px 0;
}

.inventory-editor__actions {
  display: flex;
  gap: 8px;
  margin-top: 24px;
}

.inventory-editor__actions .btn-danger {
  margin-left: auto;
}
</style>
